import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Box,
    Flex,
    Switch,
    Text,
    Button,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    useColorModeValue,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuItemOption,
    MenuOptionGroup,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    SliderMark,
    Input,
    Tooltip,
    Icon
} from '@chakra-ui/react';
import { useState } from 'react';
import {
    ArrowCircleDownIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CloseCircleIcon,
    CollectionIcon,
    DownloadIcon,
    ExternalLinkIcon,
    FlashIcon,
    LearnIcon,
    LinkIcon,
    MouseIcon,
    RefreshIcon,
    TrashIcon,
    UploadIcon
} from './UiElements';
import usePersistStore from '../stores/persistStore';
import { AnimatePresence, motion } from 'framer-motion';
import useStore from '../stores/store';
import { tooltipAnimation } from './customTheme';
import ShortcutRecorder from './ShortcutRecorder';
import {
    exportLocalData,
    exportLocalDataToCSV,
    exportLocalDataToHTML,
    exportToBookmarks
} from '../../platformFiles/utils/exportUtils';
import { PLATFORM } from '../../platformFiles/utils/platformConstants';

const SettingsDrawer = ({ onClose }) => {
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const [tabIndex, setTabIndex] = useState(0);
    const handleTabChange = (index) => {
        setTabIndex(index);
    };

    //general
    const openNewTabState = usePersistStore((state) => state.openNewTabState);
    const toggleOpenNewTab = () => {
        usePersistStore.getState().toggleOpenNewTab();
    };
    const jumpNewTabState = usePersistStore((state) => state.jumpNewTabState);
    const toggleJumpNewTab = () => {
        usePersistStore.getState().toggleJumpNewTab();
    };
    //appearance
    const showURL = usePersistStore((state) => state.showURL);
    const toggleShowURL = () => {
        usePersistStore.getState().toggleShowURL();
    };
    const showWorkspaceEmoji = usePersistStore(
        (state) => state.showWorkspaceEmoji
    );
    const toggleShowWorkspaceEmoji = () => {
        usePersistStore.getState().toggleWorkspaceEmoji();
    };
    const ShowTrashZone = usePersistStore((state) => state.showTrashZone);
    const toggleShowTrashZone = () => {
        usePersistStore.getState().toggleTrashZone();
    };
    //data
    const exportData = ({ format }) => {
        console.log('exportData', format);
    };
    const importData = () => {
        console.log('importData');
    };
    const openShortcutsModal = () => {
        console.log('openShortcutsModal');
    };
    const retakeTour = () => {
        onClose();
        useStore.getState().retakeTour();
    };

    return (
        <Tabs
            position="relative"
            variant="unstyled"
            index={tabIndex}
            onChange={handleTabChange}
        >
            <TabList pt="4px" px="20px">
                <StyledTab index={0} tabIndex={tabIndex}>
                    General
                </StyledTab>
                <StyledTab index={1} tabIndex={tabIndex}>
                    Appearance
                </StyledTab>
                <StyledTab index={2} tabIndex={tabIndex}>
                    Data
                </StyledTab>
            </TabList>
            <TabPanels
                px="4px"
                bg={menuBg}
                borderTop="var(--chakra-colors-teBorder)"
                borderBottom="var(--chakra-colors-teBorder)"
                minH="calc(100vh - 200px)"
            >
                <TabPanel h="calc(100vh - 202px)" overflow={'auto'}>
                    <SetRow
                        title="Open links in new tab"
                        details=""
                        value={openNewTabState}
                        action={toggleOpenNewTab}
                        icon="link"
                        type="switch"
                    />
                    <SetRow
                        title="Jump/focus opened tab"
                        details=""
                        value={jumpNewTabState}
                        action={toggleJumpNewTab}
                        icon="highlight"
                        type="switch"
                        disabled={!openNewTabState}
                    />
                    <AccordRow
                        title="Keyboard shortcuts"
                        details="Change default key-bindings"
                        action={openShortcutsModal}
                        icon="shortcuts"
                        type="button"
                        buttonText="Edit"
                    />
                    <Text
                        fontSize="12px"
                        color="emptyStateText"
                        mt="16px"
                        mb="8px"
                        fontWeight={600}
                    >
                        Guide & Help
                    </Text>
                    <SetRow
                        title="Retake tour"
                        details="See the extension walkthrough(6 steps)"
                        action={retakeTour}
                        icon="tour"
                        type="button"
                        buttonText=""
                        disabled={PLATFORM === 'web' ? true : false}
                    />
                </TabPanel>
                <TabPanel>
                    <SliderOption />
                    <SetRow
                        title="Show site URL"
                        details="Toggle sites URL being shown"
                        value={showURL}
                        action={toggleShowURL}
                        icon="link"
                        type="switch"
                    />
                    <SetRow
                        title="Show workspace emoji"
                        details="Show current workspace emoji next to categories"
                        value={showWorkspaceEmoji}
                        action={toggleShowWorkspaceEmoji}
                        icon="collection"
                        type="switch"
                    />
                    <SetRow
                        title="Show trash drop-zone"
                        details="Drop items off into the red area to delete them"
                        value={ShowTrashZone}
                        action={toggleShowTrashZone}
                        icon="trash"
                        type="switch"
                    />
                </TabPanel>
                <TabPanel>
                    <SetRow
                        title="Export data"
                        details="Export current workspace"
                        action={exportData}
                        icon="download"
                        type="button"
                        buttonText="Export"
                    />
                    <SetRow
                        title="Import data"
                        details="Import a workspace from a file"
                        action={importData}
                        icon="upload"
                        type="button"
                        buttonText="Import"
                        disabled
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

export default SettingsDrawer;

const SetRow = ({
    title,
    details,
    value = false,
    action,
    icon,
    type,
    buttonText = '',
    disabled = false
}) => {
    const urlText = useColorModeValue('gray.400', '#A1A2A4');
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 0.5)', '#21252850');
    const iconBox = useColorModeValue('gray.100', '#323435');

    const ICONTOSHOW = {
        link: <ExternalLinkIcon />,
        highlight: <MouseIcon />,
        collection: <CollectionIcon />,
        trash: <TrashIcon />,
        download: <DownloadIcon />,
        upload: <UploadIcon />,
        tour: <LearnIcon />
    };

    return (
        <Flex
            mb="8px"
            p="8px 12px"
            minH="61px"
            alignItems={'center'}
            rounded="10px"
            border="var(--chakra-colors-teBorder)"
            bg={pickerBg}
            opacity={disabled ? 0.5 : 1}
            pointerEvents={disabled ? 'none' : 'auto'}
            userSelect={disabled ? 'none' : 'auto'}
        >
            <Box mr="12px" rounded="8px" p="8px" bg={iconBox}>
                {ICONTOSHOW[icon] ?? ''}
            </Box>
            <Box>
                <Text fontSize="14px" fontWeight={'600'}>
                    {title}
                </Text>
                {details && (
                    <Text
                        opacity={0.725}
                        fontSize="12px"
                        color="secondaryText"
                        isTruncated
                    >
                        {details}
                    </Text>
                )}
            </Box>
            <Flex ml="auto" flexWrap={'nowrap'}>
                {type === 'switch' ? (
                    <Switch size="md" onChange={action} isChecked={value} />
                ) : (
                    <>
                        {title === 'Export data' ? (
                            <ExportTypeMenu />
                        ) : (
                            <Button size="sm" onClick={action} rounded="8px">
                                {icon === 'tour' && (
                                    <RefreshIcon
                                        color="var(--chakra-colors-iconMd)"
                                        size="16px"
                                    />
                                )}
                                {buttonText}
                                {icon === 'shortcuts' && (
                                    <Box ml="4px">
                                        <ChevronDownIcon />
                                    </Box>
                                )}
                            </Button>
                        )}
                    </>
                )}
            </Flex>
        </Flex>
    );
};

const AccordRow = ({
    title,
    details,
    value = false,
    action,
    icon,
    type,
    buttonText = '',
    disabled = false
}) => {
    const urlText = useColorModeValue('gray.400', '#A1A2A4');
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 0.5)', '#21252850');
    const iconBox = useColorModeValue('gray.100', '#323435');
    const text = useColorModeValue('#1A202C', '#FFF');
    const iconColor = useColorModeValue('gray.600', '#B3BAC6');
    const ICONTOSHOW = {
        shortcuts: <FlashIcon />
    };

    const [isOpen, setIsOpen] = useState(false);

    const shortcuts = [
        { id: 1, title: 'Create group', shortcut: '⌘/Ctrl + G' },
        { id: 2, title: 'Search', shortcut: '⌘/Ctrl + K' },
        { id: 3, title: 'Workspace grid', shortcut: 'Space' },
        { id: 4, title: 'Open bin', shortcut: '⌘/Ctrl + Z' }
    ];

    return (
        <Box
            mb="8px"
            rounded="10px"
            border="var(--chakra-colors-teBorder)"
            bg={pickerBg}
            opacity={disabled ? 0.5 : 1}
            pointerEvents={disabled ? 'none' : 'auto'}
            userSelect={disabled ? 'none' : 'auto'}
        >
            <Flex alignItems={'center'} minH="61px" p="8px 12px">
                <Box mr="12px" rounded="8px" p="8px" bg={iconBox}>
                    {ICONTOSHOW[icon] ?? ''}
                </Box>
                <Box>
                    <Text fontSize="14px" fontWeight={'600'}>
                        {title}
                    </Text>
                    {details && (
                        <Text
                            opacity={0.725}
                            fontSize="12px"
                            color="secondaryText"
                        >
                            {details}
                        </Text>
                    )}
                </Box>
                <Box ml="auto">
                    <Button
                        size="sm"
                        onClick={() => setIsOpen(!isOpen)}
                        rounded="8px"
                    >
                        {buttonText}
                        <Box ml="4px" mb="2px">
                            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </Box>
                    </Button>
                </Box>
            </Flex>
            {isOpen && (
                <Box p="8px 12px 16px">
                    {shortcuts.map((shortcut) => (
                        <Box key={shortcut.id} pl="42px">
                            <Flex mt="12px" mb="8px">
                                {/* <Icon
                                    my="auto"
                                    color={iconColor}
                                    name="addSpace"
                                    mr="12px"
                                    fontSize="18px"
                                /> */}
                                <Text
                                    letterSpacing="0.30px"
                                    style={{
                                        color: text,
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}
                                >
                                    {shortcut.title}
                                </Text>
                            </Flex>
                            <ShortcutRecorderMiddle type={shortcut.title} />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const ShortcutRecorderMiddle = ({ type }) => {
    const [indexSel, setIndexSel] = useState(null);
    const isUsingMac =
        //@ts-ignore
        navigator?.userAgentData?.platform?.indexOf('macOS') >= 0;
    const shortcuts = [
        { id: 1, title: 'Create group', shortcut: '⌘/Ctrl + G' },
        { id: 2, title: 'Search', shortcut: '⌘/Ctrl + K' },
        { id: 3, title: 'Workspace grid', shortcut: 'Space' },
        { id: 4, title: 'Open bin', shortcut: '⌘/Ctrl + Z' }
    ];
    if (type === 'Create group') {
        return (
            <ShortcutRecorder
                type={'shortcutCreateNewGroup'}
                defaultValue={isUsingMac ? 'meta+g' : 'ctrl+g'}
                setIndexSel={setIndexSel}
                indexSel={indexSel}
                index={1}
                isUsingMac={isUsingMac}
            />
        );
    } else if (type === 'Workspace grid') {
        return (
            <ShortcutRecorder
                type={'shortcutOpenWsGrid'}
                defaultValue="space"
                setIndexSel={setIndexSel}
                indexSel={indexSel}
                index={1}
                isUsingMac={isUsingMac}
            />
        );
    } else if (type === 'Open bin') {
        return (
            <ShortcutRecorder
                type={'shortcutOpenBin'}
                defaultValue={isUsingMac ? 'meta+z' : 'ctrl+z'}
                setIndexSel={setIndexSel}
                indexSel={indexSel}
                index={1}
                isUsingMac={isUsingMac}
            />
        );
    } else if (type === 'Search') {
        return (
            <ShortcutRecorder
                type={'shortcutSearch'}
                defaultValue={isUsingMac ? 'meta+k' : 'ctrl+k'}
                setIndexSel={setIndexSel}
                indexSel={indexSel}
                index={1}
            />
        );
    } else return null;
};

const SliderOption = ({}) => {
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 0.5)', '#21252850');

    /* const ICONTOSHOW = {
        link: <ExternalLinkIcon />
    }; */

    const labelStyles = {
        mt: '16px',
        fontSize: 'sm'
    };

    let itemSize = usePersistStore.getState().itemSize;
    let initSize = itemSize
        ? itemSize === 'small'
            ? 0
            : itemSize === 'medium'
            ? 1
            : 2
        : 2;
    const [sliderValue, setSliderValue] = useState(initSize);
    const setSliderMiddle = (value) => {
        let newWidth = value === 0 ? 'small' : value === 1 ? 'medium' : 'large';
        usePersistStore.setState({ itemSize: newWidth });
        setSliderValue(value);
    };

    return (
        <Box
            mb="8px"
            p="0px 0px 32px"
            minH="61px"
            alignItems={'center'}
            rounded="10px"
            overflow={'hidden'}
            border="var(--chakra-colors-teBorder)"
            bg={pickerBg}
        >
            <MiniItem sliderValue={sliderValue} />
            <Flex px="12px" mt="4px">
                <Box mt="8px">
                    <Text fontSize="14px" fontWeight={'600'}>
                        Item size
                    </Text>
                </Box>
                <Slider
                    onChange={(v) => setSliderMiddle(v)}
                    mt="4px"
                    defaultValue={sliderValue}
                    min={0}
                    max={2}
                    step={1}
                    w="194px"
                    ml="auto"
                    mr="8px"
                >
                    <SliderMark value={0} {...labelStyles}>
                        Small
                    </SliderMark>
                    <SliderMark value={1} {...labelStyles} ml="-24px">
                        Medium
                    </SliderMark>
                    <SliderMark value={2} {...labelStyles} ml="-34px">
                        Large
                    </SliderMark>
                    <SliderTrack>
                        <SliderFilledTrack bg="#54a5fe" />
                    </SliderTrack>
                    <SliderThumb
                        boxSize={5}
                        border="var(--chakra-colors-teBorder)"
                    />
                </Slider>
            </Flex>
        </Box>
    );
};

const MiniItem = ({ sliderValue }) => {
    const showURL = usePersistStore((state) => state.showURL);
    const iconBox = useColorModeValue('gray.100', '#323435');
    const favIconShadow = useColorModeValue(
        '0 1px 4px -1px rgba(78,80,81,0.4), 0 1px 2px 0px rgba(78,80,81,0.22)', //Inner shadow: inset 0 -2px 3px -1px rgba(78,80,81,0.12)
        ''
    );
    const hoverShadow = useColorModeValue(
        '0 2px 6px 0px rgb(135 145 155 / 30%), 0 0px 0px 1px rgb(135 145 155 / 8%)',
        '0 2px 8px -3px rgba(0,0,0,0.90), 0 0px 0px 1px rgb(0 0 0 / 20%)'
    );

    const favBg = useColorModeValue('#FFF', '#24272b');
    const skeletonBg = useColorModeValue('gray.100', '#323435');
    const bg = useColorModeValue('#f4f5f7', '#0c0f11');

    const pOuter =
        sliderValue === 0
            ? '6px 6px'
            : sliderValue === 1
            ? '8px 8px'
            : '13px 10px';
    const outerFav =
        sliderValue === 0 ? '24px' : sliderValue === 1 ? '28px' : '32px';
    const innerFav =
        sliderValue === 0 ? '16px' : sliderValue === 1 ? '20px' : '24px';

    return (
        <Flex
            minH="82px"
            p="12px"
            bg={bg}
            borderBottom={'var(--chakra-colors-teBorder)'}
            alignItems={'center'}
        >
            <Flex
                flex="1"
                p={pOuter}
                boxShadow={hoverShadow}
                rounded={'10px'}
                bg={favBg}
            >
                <Flex
                    h={outerFav}
                    w={outerFav}
                    rounded="8px"
                    boxShadow={favIconShadow}
                    bg={favBg}
                    alignItems={'center'}
                >
                    <Box
                        h={innerFav}
                        w={innerFav}
                        bg={skeletonBg}
                        rounded="6px"
                        m="auto"
                    />
                </Flex>
                <Box my="auto" ml={sliderValue !== 2 ? '12px' : '16px'}>
                    <Box h="12px" w="160px" bg={skeletonBg} rounded="4px" />
                    {sliderValue !== 0 && showURL && (
                        <Box
                            h="12px"
                            w="124px"
                            bg={skeletonBg}
                            rounded="4px"
                            mt="4px"
                        />
                    )}
                </Box>
            </Flex>
        </Flex>
    );
};

const StyledTab = ({ index, tabIndex, children }) => {
    const title = useColorModeValue('gray.500', 'gray.600');
    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const bgActive = useColorModeValue(
        'var(--chakra-colors-chakra-border-color)',
        '#2a2e30'
    );

    return (
        <Tab
            outline={'none'}
            p="12px 16px"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            roundedTop="10px"
            transition={'0.1s'}
            fontWeight={600}
            fontSize={'14px'}
            color={
                //selected or not
                tabIndex === index ? '#3690fb' : title
            }
            _hover={{ color: '#3690fb' }}
            _active={{
                bg: bgActive
            }}
            _focus={{ boxShadow: 'none' }}
            borderTop={
                tabIndex === index
                    ? 'var(--chakra-colors-teBorder)'
                    : '1px solid transparent'
            }
            borderLeft={
                tabIndex === index
                    ? 'var(--chakra-colors-teBorder)'
                    : '1px solid transparent'
            }
            borderRight={
                tabIndex === index
                    ? 'var(--chakra-colors-teBorder)'
                    : '1px solid transparent'
            }
            bg={tabIndex === index ? menuBg : 'transparent'}
            mb="-1px"
        >
            {children}
        </Tab>
    );
};

const ExportTypeMenu = () => {
    const tabGroups = useStore((state) => state.tabGroups);
    const [menuOpen, setMenuOpen] = useState(false);

    const menuBg = useColorModeValue('#FFF', '#262A2D');
    const lightBg = useColorModeValue('gray.50', '#31363a');

    const [selectedExportType, setSelectedExportType] = useState('JSON');

    const handleMenuItemClick = (type) => {
        setSelectedExportType(type);
        setMenuOpen(false);
    };

    const menuProps = {
        bg: { menuBg },
        p: '8px 12px',
        rounded: '6px',
        fontWeight: 500,
        transition: '0.05s',
        //margin: '4px',
        _hover: { bg: lightBg },
        iconSpacing: '12px'
    };

    //Export data
    const exportMiddle = () => {
        if (selectedExportType === 'CSV') {
            exportLocalDataToCSV(tabGroups);
        } else if (selectedExportType === 'HTML') {
            exportLocalDataToHTML(tabGroups);
        } else if (selectedExportType === 'Bookmarks') {
            exportToBookmarks(tabGroups);
        } else {
            exportLocalData(tabGroups);
        }
    };

    return (
        <>
            <Menu>
                {({ isOpen }) => (
                    <>
                        <MenuButton
                            size="sm"
                            rounded="8px"
                            as={Button}
                            rightIcon={
                                !menuOpen ? (
                                    <ChevronDownIcon />
                                ) : (
                                    <ChevronUpIcon />
                                )
                            }
                            ml="auto"
                            transition="0.12s"
                            //onClick={() => setMenuOpen((prev) => !prev)}
                            mr="4px"
                        >
                            {selectedExportType}
                        </MenuButton>
                        <AnimatePresence>
                            {isOpen && (
                                <MenuList
                                    as={motion.div}
                                    animate={{
                                        y: 0,
                                        opacity: 1,
                                        transition: {
                                            duration: 0.12,
                                            ease: [0.17, 0.67, 0.83, 0.67]
                                        }
                                    }}
                                    boxShadow={
                                        'var(--chakra-colors-popoverShadowLight)'
                                    }
                                    border="var(--chakra-colors-teBorder)"
                                    p="4px"
                                    rounded="8px"
                                    bg={menuBg}
                                >
                                    <MenuOptionGroup
                                        defaultValue={selectedExportType}
                                        title="File Type"
                                        type="radio"
                                        onChange={handleMenuItemClick}
                                    >
                                        <MenuItemOption
                                            value="JSON"
                                            fontWeight="600"
                                            fontSize="14px"
                                            {...menuProps}
                                        >
                                            JSON
                                        </MenuItemOption>
                                        <MenuItemOption
                                            value="HTML"
                                            fontWeight="600"
                                            fontSize="14px"
                                            {...menuProps}
                                        >
                                            HTML
                                        </MenuItemOption>
                                        <MenuItemOption
                                            value="CSV"
                                            fontWeight="600"
                                            fontSize="14px"
                                            {...menuProps}
                                        >
                                            CSV
                                        </MenuItemOption>
                                        <MenuItemOption
                                            value="Bookmarks"
                                            fontWeight="600"
                                            fontSize="14px"
                                            {...menuProps}
                                        >
                                            Browser Bookmarks
                                        </MenuItemOption>
                                    </MenuOptionGroup>
                                </MenuList>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </Menu>
            <Button size="sm" onClick={exportMiddle} rounded="8px">
                Export
            </Button>
        </>
    );
};

/* const AccordionRow = ({ title, children }) => {
    return (
        <AccordionItem>
            <h2>
                <AccordionButton p="8px 24px">
                    <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontSize="14px"
                        fontWeight={600}
                    >
                        {title}
                    </Box>
                    <AccordionIcon />
                </AccordionButton>
            </h2>
            <AccordionPanel pb={4} p="8px 24px">
                {children}
            </AccordionPanel>
        </AccordionItem>
    );
}; */
